import { useCallback, useEffect, useState } from 'react';
import type {
  SearchCategorySelection,
  SearchResultHit,
} from 'components/SearchOverlay/types';
import {
  isSameCategorySelection,
  type SearchOverlaySnapshot,
} from 'components/SearchOverlay/types';
import {
  clearSearchSnapshot,
  restoreSearchSnapshot,
  writeSearchSnapshot,
} from 'components/SearchOverlay/searchOverlayStorage';

/**
 * Owns the SearchOverlay's query + filter state and mirrors the last completed
 * search to sessionStorage. Storage is written only by `persistSearchResults`
 * and cleared when the search is reset — never mid-typing, so the setters stay
 * plain state setters. A fresh same-scope snapshot is restored on first render.
 */

interface UsePersistedSearchStateResult {
  query: string;
  setQuery: (value: string) => void;
  selectedLocationId: string | null;
  setSelectedLocationId: (id: string | null) => void;
  selectedCategory: SearchCategorySelection | null;
  setSelectedCategory: (category: SearchCategorySelection | null) => void;
  clearSearch: () => void;
  cachedResults: SearchResultHit[] | undefined;
  cachedResultsQuery: string | undefined;
  persistSearchResults: (
    forQuery: string,
    forLocationId: string | null,
    forCategory: SearchCategorySelection | null,
    results: SearchResultHit[]
  ) => void;
}

export const usePersistedSearchState = (
  accessToken: string | null | undefined,
  orgId: string | null | undefined
): UsePersistedSearchStateResult => {
  const [query, setQuery] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(
    null
  );
  const [selectedCategory, setSelectedCategory] =
    useState<SearchCategorySelection | null>(null);
  // The snapshot restored on mount, kept so its results back the UI until the
  // live search refetches.
  const [restoredSearch, setRestoredSearch] =
    useState<SearchOverlaySnapshot | null>(null);
  const [hydrated, setHydrated] = useState(false);

  // Restore once, after the token + org are known.
  useEffect(() => {
    if (hydrated || !accessToken || !orgId) {
      return;
    }
    setHydrated(true);

    const restored = restoreSearchSnapshot(accessToken, orgId);
    if (restored) {
      setQuery(restored.query);
      setSelectedLocationId(restored.selectedLocationId);
      setSelectedCategory(restored.selectedCategory);
      setRestoredSearch(restored);
    }
  }, [hydrated, accessToken, orgId]);

  // Clear storage once the search is fully reset. Guarded by `hydrated` so
  // mounting cannot wipe a snapshot before it is restored.
  useEffect(() => {
    if (
      hydrated &&
      query === '' &&
      selectedLocationId === null &&
      selectedCategory === null
    ) {
      clearSearchSnapshot();
    }
  }, [hydrated, query, selectedLocationId, selectedCategory]);

  // The only writer: a completed search, persisted for restore here or in
  // another app sharing this origin.
  const persistSearchResults = useCallback(
    (
      forQuery: string,
      forLocationId: string | null,
      forCategory: SearchCategorySelection | null,
      results: SearchResultHit[]
    ) => {
      if (!accessToken || !orgId) {
        return;
      }
      writeSearchSnapshot({
        sessionToken: accessToken,
        orgId,
        savedAt: Date.now(),
        query: forQuery,
        selectedLocationId: forLocationId,
        selectedCategory: forCategory,
        results,
      });
    },
    [accessToken, orgId]
  );

  const clearSearch = useCallback(() => {
    setQuery('');
    setSelectedLocationId(null);
    setSelectedCategory(null);
    setRestoredSearch(null);
  }, []);

  // Restored results are valid only while the query and filters still match
  // the search they came from.
  const cachedResults =
    restoredSearch &&
    restoredSearch.query === query &&
    restoredSearch.selectedLocationId === selectedLocationId &&
    isSameCategorySelection(restoredSearch.selectedCategory, selectedCategory)
      ? restoredSearch.results
      : undefined;
  const cachedResultsQuery = cachedResults ? restoredSearch?.query : undefined;

  return {
    query,
    setQuery,
    selectedLocationId,
    setSelectedLocationId,
    selectedCategory,
    setSelectedCategory,
    clearSearch,
    cachedResults,
    cachedResultsQuery,
    persistSearchResults,
  };
};
